import React from 'react';
import {
  Form,
  useSubmit,
  useNavigation,
  useLoaderData,
  useNavigate,
  useLocation,
  useOutlet,
  useActionData
} from 'react-router-dom';

import {
  MagnifyingGlassIcon,
  CircleStackIcon,
  ChevronDownIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import {
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
  Dialog,
  MountableDialog
} from '@oysterjs/uiv2/dialog';
import { PageTitle } from '@oysterjs/uiv2/page';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import { Button } from '@oysterjs/uiv2/button';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Description,
  ErrorMessage
} from '@oysterjs/uiv2/fieldset';
import {
  CreateKnowledgeBaseTextDocumentMutation,
  GetKnowledgeBaseDocumentDocument,
  GetKnowledgeBaseDocumentQuery,
  IndexingStatus,
  KnowledgeBaseDetailFieldsFragment,
  KnowledgeBaseDocumentDetailFieldsFragment,
  KnowledgeBaseDocumentFieldsFragment,
  UpdateKnowledgeBaseDocumentInput,
  UpdateKnowledgeBaseDocumentMutation,
  UploadKnowledgeBaseFileDocumentMutation
} from '../../api/graphql';
import { Await, TransitionDiv } from '@oysterjs/uiv2/async';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FileInput } from '@oysterjs/uiv2/file';
import { Badge } from '@oysterjs/uiv2/badge';
import { MountableSlideout } from '@oysterjs/uiv2/slideout';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { AutoResizeTextarea } from '@oysterjs/uiv2/textarea';
import { client } from '../../api/client';

const getIndexingStatusLabel = (status: IndexingStatus) => {
  switch (status) {
    case IndexingStatus.Completed:
      return 'Completed';
    case IndexingStatus.Failed:
      return 'Failed';
    case IndexingStatus.Indexing:
      return 'Indexing';
    case IndexingStatus.Unknown:
      return 'Queued';
  }
};

const DocumentStatusBadge = ({ status }: { status: IndexingStatus }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [IndexingStatus.Completed]: 'green',
          [IndexingStatus.Failed]: 'red',
          [IndexingStatus.Indexing]: 'blue',
          [IndexingStatus.Unknown]: 'neutral'
        }[status] || 'neutral'
      }
    >
      {getIndexingStatusLabel(status)}
    </Badge>
  );
};

export const KnowledgeBasePage = () => {
  const outlet = useOutlet();

  const [filter, setFilter] = React.useState('');
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }> }
    | undefined;

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

  const filterDocument = (document: KnowledgeBaseDocumentFieldsFragment) => {
    if (!filter) {
      return true;
    }

    return (
      document.title.toLowerCase().includes(filter.toLowerCase()) ||
      document.metadata?.title?.toLowerCase().includes(filter.toLowerCase()) ||
      document.metadata?.summary?.toLowerCase().includes(filter.toLowerCase()) ||
      document.createdBy.name.toLowerCase().includes(filter.toLowerCase())
    );
  };

  return (
    <PageTitle
      title="Knowledge Base"
      description="View and manage the knowledge Cara uses to operate"
    >
      <div className="space-y-4">
        <div>
          <div className="flex justify-between gap-4 flex-col xs:flex-row">
            <form className="w-full max-w-md">
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label className="sr-only" data-slot="none">
                      Filter table
                    </Label>
                    <InputGroup>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      <Input
                        type="search"
                        name="search"
                        placeholder="Start typing to filter..."
                        value={filter}
                        onChange={(e) => setFilter(e.currentTarget.value.trim())}
                      />
                    </InputGroup>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </form>
            <div className="inline-flex">
              <Menu as="div" className="relative -ml-px block">
                <MenuButton
                  className={clsx(
                    'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.sky.500)] [--btn-border:theme(colors.sky.600/80%)]',
                    // Base
                    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
                    // Sizing
                    'px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6',
                    // Focus
                    'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-primary-500',
                    // Disabled
                    'data-[disabled]:opacity-50',
                    // Icon
                    '[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]',
                    // Optical border, implemented as the button background to avoid corner artifacts
                    'border-transparent bg-[--btn-border]',
                    // Dark mode: border is rendered on `after` so background is set to button background
                    'dark:bg-[--btn-bg]',
                    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
                    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-[--btn-bg]',
                    // Drop shadow, applied to the inset `before` layer so it blends with the border
                    'before:shadow',
                    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
                    'dark:before:hidden',
                    // Dark mode: Subtle white outline is applied using a border
                    'dark:border-white/5',
                    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
                    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(theme(borderRadius.lg)-1px)]',
                    // Inner highlight shadow
                    'after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]',
                    // White overlay on hover
                    'after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]',
                    // Dark mode: `after` layer expands to cover entire button
                    'dark:after:-inset-px dark:after:rounded-lg',
                    // Disabled
                    'before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none'
                  )}
                >
                  <span>Add Document</span>
                  <ChevronDownIcon aria-hidden="true" className="size-5" />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 -mr-1 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    <MenuItem>
                      <span
                        onClick={() => setUploadDialogOpen(true)}
                        className="flex flex-row gap-2 items-center px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900 data-[focus]:outline-none"
                      >
                        <ArrowUpTrayIcon className="size-4 text-neutral-500" />
                        Upload Existing Documents
                      </span>
                    </MenuItem>
                    <MenuItem>
                      <Link
                        to="/app/knowledge/new"
                        className="flex flex-row gap-2 items-center px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900 data-[focus]:outline-none"
                      >
                        <PencilSquareIcon className="size-4 text-neutral-500" />
                        Create Document
                      </Link>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>
        <React.Suspense fallback={<KnowledgeBasePageLoader />}>
          <Await resolve={loader?.data}>
            {(data) =>
              data?.knowledgeBase && (
                <div className="px-6 rounded-lg bg-white shadow">
                  {!data.knowledgeBase?.documents?.length && (
                    <div className="text-center py-12">
                      <CircleStackIcon className="mx-auto size-12 text-neutral-400" />
                      <h3 className="mt-2 text-sm font-semibold text-neutral-900">No documents</h3>
                      <p className="mt-1 text-sm text-neutral-500">
                        You haven't added any documents to the knowledge base yet. Add a new
                        document to get started.
                      </p>
                    </div>
                  )}
                  {!!data.knowledgeBase?.documents?.length && (
                    <Table
                      dense
                      bleed
                      className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                    >
                      <TableHead>
                        <TableRow>
                          <TableHeader className="py-4">Name</TableHeader>
                          <TableHeader className="py-4">Status</TableHeader>
                          <TableHeader className="py-4">Created By</TableHeader>
                          <TableHeader className="py-4">Date Added</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.knowledgeBase.documents.filter(filterDocument).map((document) => (
                          <TableRow key={document.id} href={`/app/knowledge/${document.id}`}>
                            <TableCell>
                              <span className="truncate">{document.title}</span>
                            </TableCell>
                            <TableCell>
                              <DocumentStatusBadge status={document.indexingStatus.status} />
                            </TableCell>
                            <TableCell>{document.createdBy.name}</TableCell>
                            <TableCell>
                              {new Intl.DateTimeFormat('en-US', {
                                dateStyle: 'short',
                                timeStyle: 'short'
                              }).format(new Date(document.createdAt))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )
            }
          </Await>
        </React.Suspense>
      </div>

      <KnowledgeBaseUploadDocumentPage
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, {})}
      </AnimatePresence>
    </PageTitle>
  );
};

export const KnowledgeBasePageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading knowledge base...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const KnowledgeBaseUploadDocumentPage = (props: { open: boolean; onClose: () => void }) => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<UploadKnowledgeBaseFileDocumentMutation>;

  const [files, setFiles] = React.useState<File[]>([]);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    submit(formData, { method: 'POST', encType: 'multipart/form-data' });
  };

  React.useEffect(() => {
    if (navigation.state === 'submitting') {
      setSubmitting(true);
    } else if (navigation.state === 'idle') {
      setSubmitting(false);
    }
  }, [navigation.state]);

  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.formAction === navigation.location.pathname &&
      !!action?.data?.uploadKnowledgeBaseFileDocument?.[0]?.id
    ) {
      props.onClose();
    }
  }, [navigation.state, action?.data?.uploadKnowledgeBaseFileDocument?.[0]?.id]);

  return (
    <Dialog onClose={props.onClose} open={props.open}>
      <DialogTitle>Upload documents</DialogTitle>
      <DialogDescription>
        Upload one or more documents to the knowledge base. Cara will index these and leverage them
        proactively in everything she does.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          {!submitting && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Files</Label>
                    <Description>Currently only PDF files and images are supported.</Description>
                    <FileInput multiple onChange={(files) => setFiles(files)} />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </motion.div>
          )}
          {submitting && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
            >
              <AnimatedUpload files={files} complete={navigation.state === 'loading'} />
            </motion.div>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={props.onClose}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!files.length}
            loading={navigation.state === 'submitting'}
          >
            Upload Documents
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const AnimatedUpload = ({ files, complete }: { files: File[]; complete: boolean }) => {
  const [state, setState] = React.useState<{ name: string; size: number; progress: number }[]>(
    files.map((file) => ({ name: file.name, size: file.size, progress: 0 }))
  );

  // Upload rate, assume 512 KB/s
  const uploadRateBytesPerSecond = 512 * 1024;

  React.useEffect(() => {
    if (complete) {
      return;
    }

    const interval = setInterval(() => {
      setState((prev) => {
        // Find first incomplete file
        const incompleteIndex = prev.findIndex((item) => item.progress < 1);
        if (incompleteIndex === -1) return prev;

        return prev.map((item, index) => {
          if (index === incompleteIndex) {
            return {
              ...item,
              progress: Math.min(
                item.progress +
                  ((0.8 + Math.random() / 2.5) * uploadRateBytesPerSecond) / 10 / item.size,
                1
              )
            };
          }
          return item;
        });
      });
    }, 100);

    return () => clearInterval(interval);
  }, [complete]);

  React.useEffect(() => {
    if (complete) {
      setState((prev) => prev.map((item) => ({ ...item, progress: 1 })));
    }
  }, [complete]);

  return (
    <div className="flex flex-col gap-y-2">
      {state.map((file) => (
        <div key={file.name} className="flex flex-row items-center justify-between gap-x-4">
          <span className="flex-1 truncate text-sm text-neutral-700">{file.name}</span>
          <div className="relative size-8">
            <div className="absolute inset-0">
              <AnimatePresence>
                {complete || file.progress === 1 ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="size-full rounded-full bg-emerald-500 flex items-center justify-center"
                  >
                    <svg className="size-3 text-white" viewBox="0 0 16 16" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M13.366 4.234a.8.8 0 010 1.132l-6.4 6.4a.8.8 0 01-1.132 0l-3.2-3.2a.8.8 0 011.132-1.132L6.4 10.07l5.834-5.835a.8.8 0 011.132 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </motion.div>
                ) : (
                  <motion.svg
                    className="size-full"
                    viewBox="0 0 64 64"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.circle
                      className="text-neutral-100"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="26"
                      cx="32"
                      cy="32"
                    />
                    <motion.circle
                      className="text-sky-500"
                      strokeWidth="8"
                      initial={{ strokeDasharray: '0 163.363' }}
                      animate={{ strokeDasharray: `${file.progress * 163.363} 163.363` }}
                      transition={{ duration: 0.5 }}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="26"
                      cx="32"
                      cy="32"
                    />
                  </motion.svg>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const KnowledgeBaseDocumentCreatePage = () => {
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateKnowledgeBaseTextDocumentMutation>;
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  return (
    <KnowledgeBaseDocumentCreateOrEditPage
      editMode={false}
      initialForm={{
        title: '',
        content: ''
      }}
      onSubmit={(form) => submit(form, { method: 'POST', encType: 'application/json' })}
      validationErrors={validationErrors}
    />
  );
};

export const KnowledgeBaseDocumentEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBaseDocument?: KnowledgeBaseDocumentDetailFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.knowledgeBaseDocument) {
            return null;
          }

          const submit = useSubmit();
          const action = useActionData() as ActionResult<UpdateKnowledgeBaseDocumentMutation>;
          const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

          React.useEffect(() => {
            if (action?.validationErrors) {
              setValidationErrors(action.validationErrors);
            }
          }, [action?.validationErrors]);

          return (
            <KnowledgeBaseDocumentCreateOrEditPage
              editMode
              initialForm={{
                title: data.knowledgeBaseDocument.title || '',
                content: data.knowledgeBaseDocument.text?.content || ''
              }}
              onSubmit={(form) => submit(form, { method: 'PUT', encType: 'application/json' })}
              validationErrors={validationErrors}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const KnowledgeBaseDocumentCreateOrEditPage = (props: {
  editMode?: boolean;
  initialForm: UpdateKnowledgeBaseDocumentInput;
  onSubmit: (form: UpdateKnowledgeBaseDocumentInput) => void;
  validationErrors: ValidationError[];
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<UpdateKnowledgeBaseDocumentInput>(props.initialForm);
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.onSubmit(form);
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="3xl" className="relative">
      <DialogTitle>{props.editMode ? 'Edit document' : 'Create a new document'}</DialogTitle>
      <DialogDescription>
        {props.editMode
          ? 'Update the document and save your changes.'
          : 'Create a new document for Cara to reference as part of her knowledge base.'}
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Title</Label>
                <Description>
                  A brief, descriptive title for the document. This should be two to four words.
                </Description>
                <Input
                  className="max-w-xs"
                  value={form?.title || ''}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['title'])}
                />
                {!!getValidationError(validationErrors, ['title']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['title'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Content</Label>
                <Description>
                  The content of the document you'd like to add to Cara's knowledge base.
                </Description>
                <AutoResizeTextarea
                  className="font-mono"
                  value={form?.content || ''}
                  onChange={(e) => {
                    const content = e.currentTarget.value;
                    updateForm(['content'], (prev) => ({ ...prev, content }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['content'])}
                />
                {!!getValidationError(validationErrors, ['content']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['content'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!form}
            loading={navigation.state === 'submitting'}
          >
            {props.editMode ? 'Update Document' : 'Create Document'}
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const KnowledgeBaseDocumentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBaseDocument?: KnowledgeBaseDocumentFieldsFragment }> }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/knowledge');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Document Information">
      <React.Suspense
        fallback={
          <div className="flex justify-center">
            <Spinner size="size-6" />
          </div>
        }
      >
        <Await resolve={loader?.data}>
          {(data) =>
            data?.knowledgeBaseDocument && <DocumentPage document={data.knowledgeBaseDocument} />
          }
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const DocumentPage = (props: { document: KnowledgeBaseDocumentFieldsFragment }) => {
  const [document, setDocument] = React.useState<KnowledgeBaseDocumentFieldsFragment>(
    props.document
  );
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const navigation = useNavigation();
  const submit = useSubmit();

  const onDelete = () => submit({}, { method: 'DELETE' });

  React.useEffect(() => {
    setDocument(props.document);
  }, [props.document]);

  React.useEffect(() => {
    if (document.indexingStatus.status !== IndexingStatus.Indexing) {
      return;
    }

    const refreshDocument = async () => {
      const res = await client.query<GetKnowledgeBaseDocumentQuery>({
        query: GetKnowledgeBaseDocumentDocument,
        variables: {
          id: document.id
        }
      });

      if (res.data?.knowledgeBaseDocument) {
        setDocument(res.data.knowledgeBaseDocument);
      }
    };

    const interval = setInterval(() => refreshDocument(), 3000);
    return () => clearInterval(interval);
  }, [document.indexingStatus.status]);

  return (
    <div>
      <dl>
        <DocumentProperties document={document} />
        {document.file && <DocumentFileProperties document={document} />}
        <DocumentIndexingStatus document={document} />
        {document.metadata && <DocumentMetadata document={document} />}
      </dl>
      <div className="mt-4 flex-col items-center *:w-full sm:flex-row sm:*:w-auto w-full flex gap-2 flex-shrink-0">
        {document.file?.url && (
          <Button href={document.file?.url} target="_blank" type="button" color="white">
            View Document
          </Button>
        )}
        {document.text?.content && (
          <Button href={`/app/knowledge/${document.id}/edit`} type="button" color="white">
            Edit Document
          </Button>
        )}
        <Button onClick={() => setShowDeleteDialog(true)} type="button" color="red">
          Delete Document
        </Button>
      </div>
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} size="md">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete <span className="font-semibold">{document.title}</span>?
          This action is permanent and the file will no longer be used by Cara.
        </DialogDescription>
        <DialogActions>
          <Button
            type="button"
            color="white"
            onClick={() => setShowDeleteDialog(false)}
            disabled={navigation.state === 'submitting'}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="red"
            onClick={onDelete}
            loading={navigation.state === 'submitting'}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const DocumentProperties = (props: { document: KnowledgeBaseDocumentFieldsFragment }) => {
  const properties = [
    {
      title: 'Document Type',
      value: props.document.file ? 'Uploaded File' : 'Text'
    },
    {
      title: 'Created At',
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(props.document.createdAt))
    },
    {
      title: 'Created By',
      value: props.document.createdBy.name
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
      {properties.map((property) => (
        <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm/6 font-medium text-neutral-900">{property.title}</dt>
          <dd className="text-sm/6 text-neutral-700 sm:col-span-2">{property.value}</dd>
        </div>
      ))}
    </div>
  );
};

const DocumentFileProperties = (props: { document: KnowledgeBaseDocumentFieldsFragment }) => {
  const properties = [
    {
      title: 'File Type',
      value: props.document.file?.mimeType
    },
    {
      title: 'File Name',
      value: props.document.file?.name
    },
    {
      title: 'File Size',
      value: props.document.file?.size
        ? `${(props.document.file.size / 1024 ** Math.floor(Math.log(props.document.file.size) / Math.log(1024))).toFixed(2)} ${['B', 'KB', 'MB', 'GB', 'TB'][Math.floor(Math.log(props.document.file.size) / Math.log(1024))]}`
        : undefined
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 py-6 border-t border-neutral-200">
      {properties.map((property) => (
        <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm/6 font-medium text-neutral-900">{property.title}</dt>
          <dd className="text-sm/6 text-neutral-700 sm:col-span-2">{property.value}</dd>
        </div>
      ))}
    </div>
  );
};

const DocumentIndexingStatus = (props: { document: KnowledgeBaseDocumentFieldsFragment }) => {
  const properties = [
    {
      title: 'Indexing Status',
      value: <DocumentStatusBadge status={props.document.indexingStatus.status} />
    },
    {
      title: 'Progress',
      value: (
        <ProgressBar
          title="Indexing Progress"
          percentComplete={props.document.indexingStatus.progress || 0}
        />
      ),
      hidden: props.document.indexingStatus.status !== IndexingStatus.Indexing
    },
    {
      title: 'Error',
      value: props.document.indexingStatus.error,
      hidden: !props.document.indexingStatus.error
    },
    {
      title: 'Started At',
      value: props.document.indexingStatus.startedAt
        ? new Intl.DateTimeFormat('en-US', {
            dateStyle: 'short',
            timeStyle: 'short'
          }).format(new Date(props.document.indexingStatus.startedAt))
        : undefined,
      hidden: !props.document.indexingStatus.startedAt
    },
    {
      title: 'Completed At',
      value: props.document.indexingStatus.completedAt
        ? new Intl.DateTimeFormat('en-US', {
            dateStyle: 'short',
            timeStyle: 'short'
          }).format(new Date(props.document.indexingStatus.completedAt))
        : undefined,
      hidden: !props.document.indexingStatus.completedAt
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 py-6 border-t border-neutral-200">
      {properties
        .filter((property) => !property.hidden)
        .map((property) => (
          <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm/6 font-medium text-neutral-900">{property.title}</dt>
            <dd className="text-sm/6 text-neutral-700 sm:col-span-2">{property.value}</dd>
          </div>
        ))}
    </div>
  );
};

const DocumentMetadata = (props: { document: KnowledgeBaseDocumentFieldsFragment }) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 py-6 border-t border-neutral-200">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Title</dt>
        <dd className="text-sm/6 text-neutral-700">{props.document.metadata?.title}</dd>
      </div>
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Summary</dt>
        <dd className="text-sm/6 text-neutral-700">{props.document.metadata?.summary}</dd>
      </div>
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Purpose</dt>
        <dd className="text-sm/6 text-neutral-700">{props.document.metadata?.purpose}</dd>
      </div>
    </div>
  );
};

const ProgressBar = (props: { title: string; percentComplete: number }) => {
  return (
    <div>
      <div>
        {props.title}:{' '}
        {new Intl.NumberFormat('en-US', {
          style: 'percent',
          maximumFractionDigits: 2
        }).format(props.percentComplete)}
      </div>
      <div className="mt-1">
        <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-700">
          <div
            className={`h-2 rounded-full bg-gradient-to-r to-primary-300 from-primary-600 bg-[size:200%_100%] animate-progress-bar transition-all ease-in-out duration-1000`}
            style={{ width: `${props.percentComplete * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};
